import { useAgentContext } from '@Providers';

import { AgentOptionItem, OSAMessage, ThinkingStep } from '@Models';
import MessageAgent from './MessageAgent';
import MessageUser from './MessageUser';
import Thinking from './Thinking';

interface MessageListProps {
  messages: OSAMessage[];
  isThinking?: boolean;
  isGenerating?: boolean;
  thinkingSteps?: ThinkingStep[];
  thinkingSeconds?: number | null;
  /** Interaction the live runtime steps belong to. */
  turnTimestamp?: string | null;
  thinkingStartedAtMs?: number | null;
  onSelectOption?: (message: OSAMessage, option: AgentOptionItem) => void;
  onConfirmSelection?: (message: OSAMessage, selected: AgentOptionItem[]) => void;
  onConfirmChoices?: (
    message: OSAMessage,
    answered: { row: AgentOptionItem; choice: { id: string; label: string } }[],
  ) => void;
  onRegenerate?: (message: OSAMessage) => void;
}

const MessageList = ({
  messages,
  isThinking = false,
  isGenerating = false,
  thinkingSteps = [],
  thinkingSeconds = null,
  turnTimestamp = null,
  thinkingStartedAtMs = null,
  onSelectOption,
  onConfirmSelection,
  onConfirmChoices,
  onRegenerate,
}: MessageListProps) => {
  const { mode, selectedAgent } = useAgentContext();

  const isFullscreen = mode === 'fullscreen';
  // Also covers the gap between the first token and its message reaching the list.
  const showLivePanel = isThinking || (isGenerating && messages[messages.length - 1]?.role !== 'assistant');
  const wrapperClass = isFullscreen ? 'mx-auto w-full min-w-0 max-w-3xl px-5 py-5 space-y-5' : 'min-w-0 p-6 space-y-6';

  return (
    <div className={wrapperClass}>
      {messages.map((message, index) => {
        if (message.role === 'user') {
          return <MessageUser key={message.id} content={message.content} />;
        }
        if (message.role === 'assistant') {
          // A message is "streaming" only when it's the literal last item in
          // the list AND the runtime is generating. Using lastAssistantIndex
          // here would mark the previous assistant as streaming during the
          // brief window between adding the new user message and the new
          // assistant message appearing, which hides its options on click.
          const isLatest = index === messages.length - 1;
          const isStreaming = isGenerating && isLatest;
          const optionsActive = isLatest && !isStreaming;
          // The runtime holds the freshest steps for the current turn; history covers the rest.
          const isLiveTurn = turnTimestamp !== null && message.interactionTimestamp === turnTimestamp;
          const steps = isLiveTurn && thinkingSteps.length > 0 ? thinkingSteps : message.thinkingSteps;
          const seconds = isLiveTurn && thinkingSteps.length > 0 ? thinkingSeconds : message.thinkingSeconds;
          return (
            <MessageAgent
              key={message.id}
              content={message.content}
              timestamp={message.timestamp}
              conversationId={message.conversationId}
              interactionTimestamp={message.interactionTimestamp}
              feedback={message.feedback}
              isLatest={isLatest}
              options={message.options}
              selectedOptionId={message.selectedOptionId}
              optionsDisabled={!optionsActive}
              multiselectOptions={message.multiselectOptions}
              selectedOptionIds={message.selectedOptionIds}
              choicegridOptions={message.choicegridOptions}
              choicegridAnswers={message.choicegridAnswers}
              generatedFiles={message.generatedFiles}
              thinkingSteps={steps}
              thinkingSeconds={seconds}
              isStreaming={isStreaming}
              onSelectOption={onSelectOption ? (option) => onSelectOption(message, option) : undefined}
              onConfirmSelection={onConfirmSelection ? (selected) => onConfirmSelection(message, selected) : undefined}
              onConfirmChoices={onConfirmChoices ? (answered) => onConfirmChoices(message, answered) : undefined}
              onRegenerate={optionsActive && onRegenerate ? () => onRegenerate(message) : undefined}
            />
          );
        }
        return null;
      })}

      {showLivePanel && (
        <Thinking steps={thinkingSteps} startedAtMs={thinkingStartedAtMs} lng={selectedAgent?.language} />
      )}
    </div>
  );
};

export default MessageList;
